import { setup, assign, fromPromise } from 'xstate';
import {
  CandidateRefusal,
  Context,
  Event,
  State,
  EventType,
  createJobEvent,
  JobPayload,
  fetchJobListEvent,
  fetchJobByIdEvent,
  fetchCandidatesEvent,
  updateJobEvent,
  sendCandidatesEvent,
  assignHiringmanagersEvent,
  fetchJobHiringmanagersEvent,
  getCandidateEvent,
  addFeedbackEvent,
  getFeedbackEvent,
  fetchSharedCandidatesEvent,
  updateCandidateStatusEvent,
  getCandidateDownloadCVUrlEvent,
  shareCandidateEvent,
  Status,
  assignRecruiterEvent,
  fetchJobAutopopulatedValuesEvent,
} from './jobs.fsm.types';
import axios from '../../axiosConfig';
import { AxiosError } from 'axios';

const MACHINE_ID = 'JobsFSM';

/**
 * A 403 on a candidate, read as a refusal: in a test workspace, a real job
 * seeker the tester can see in the list but not open. Keeps the server's own
 * reason when it is a short sentence (the same 200-character bound the error
 * toast uses), so a framework body never reaches the page. Undefined for any
 * other failure.
 */
export const candidateRefusal = (
  error: unknown,
): CandidateRefusal | undefined => {
  const response = (error as AxiosError<{ message?: unknown }> | undefined)
    ?.response;
  if (response?.status !== 403) return undefined;
  const message = response.data?.message;
  return typeof message === 'string' &&
    message.trim().length > 0 &&
    message.length <= 200
    ? { message: message.trim() }
    : {};
};

const jobsSetup = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
  },
  actions: {
    // Entering a candidate load drops whatever candidate was held, so the
    // previous profile can never show under the new one while it loads (or
    // after it fails), and records which candidate the reply must be for.
    startCandidateLoad: assign({
      isLoading: true,
      candidate: undefined,
      // Recruiter feedback belongs to the candidate it was fetched for; the
      // modal fetches again when it opens.
      candidateFeedback: null,
      candidateLoadFailedFor: undefined,
      candidateLoadRefusal: undefined,
      requestedCandidateId: ({ event }) =>
        (event as getCandidateEvent).payload.candidateId,
    }),
  },
  actors: {
    fetchJobList: fromPromise(
      async ({
        input,
      }: {
        input: {
          pageNumber: number;
          pageSize: number;
          sort?: string;
          order?: string;
          status?: string;
          includeSynthetic?: boolean;
        };
      }) => {
        const { pageNumber, pageSize, sort, order, status, includeSynthetic } =
          input;
        // Sent only when asked for: the backend hides synthetic customers' jobs by default, and every
        // role but Super Admin ignores it.
        const res = await axios.get(
          `/api/jobs?pageNumber=${pageNumber}&pageSize=${pageSize}&sort=${sort}&order=${order}${status ? `&status=${status}` : ''}${includeSynthetic ? '&includeSynthetic=true' : ''}`,
        );
        return res;
      },
    ),
    createJob: fromPromise(
      async ({ input }: { input: { job: JobPayload } }) => {
        const res = await axios.post('/api/jobs', input.job);
        return res;
      },
    ),
    fetchJobById: fromPromise(async ({ input }: { input: { id: number } }) => {
      const res = await axios.get(`/api/jobs/${input.id}`);
      return res;
    }),
    updateJob: fromPromise(
      async ({ input }: { input: { id: number; job: JobPayload } }) => {
        const res = await axios.put(`/api/jobs/${input.id}`, input.job);
        return res;
      },
    ),
    fetchSharedCandidates: fromPromise(
      async ({
        input,
      }: {
        input: { pageNumber: number; pageSize: number };
      }) => {
        const res = await axios.get(
          `/api/shared-candidates?pageNumber=${input.pageNumber}&pageSize=${input.pageSize}`,
        );
        return res;
      },
    ),
    fetchCandidates: fromPromise(
      async ({
        input,
      }: {
        input: {
          jobId: number;
          pageNumber: number;
          pageSize: number;
          fitScore?: number;
          statuses?: Status[];
          hasMandatorySkills?: boolean;
          hasMandatoryTitleMatch?: boolean;
          maxDistance?: number;
          candidatesWithNoLocation?: boolean;
          isApplicant?: boolean;
          sort?: string;
          order?: string;
        };
      }) => {
        // Convert to a query string
        const queryFilterString = Object.entries({
          fitScore: input.fitScore,
          statuses: input.statuses,
          hasMandatorySkills: input.hasMandatorySkills,
          hasMandatoryTitleMatch: input.hasMandatoryTitleMatch,
          maxDistance: input.maxDistance,
          candidatesWithNoLocation: input.candidatesWithNoLocation,
          isApplicant: input.isApplicant,
          sort: input.sort,
          order: input.order,
        })
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, value]) => value !== undefined)
          .map(
            ([key, value]) =>
              value &&
              `${encodeURIComponent(key)}=${Array.isArray(value) ? value.join(',') : encodeURIComponent(value)}`,
          )
          .join('&');
        const res = await axios.get(
          `/api/jobs/${input.jobId}/candidates?pageNumber=${input.pageNumber}&pageSize=${input.pageSize}&${queryFilterString}`,
        );
        return res;
      },
    ),
    fetchHiringManagers: fromPromise(async () => {
      const res = await axios.get(`/api/users?role=HIRING_MANAGER`);
      return res;
    }),
    fetchRecruiters: fromPromise(async () => {
      const res = await axios.get(`/api/users?role=RECRUITER&pageSize=100`);
      return res;
    }),
    fetchJobHiringManagers: fromPromise(
      async ({ input }: { input: { jobId: number } }) => {
        const res = await axios.get(`/api/jobs/${input.jobId}/hiring-managers`);
        return res;
      },
    ),
    assignHiringManagers: fromPromise(
      async ({
        input,
      }: {
        input: { jobId: number; hiringManagerId: number };
      }) => {
        const res = await axios.patch(
          `/api/jobs/${input.jobId}/hiring-managers`,
          { hiringManagerId: input.hiringManagerId },
        );
        return res;
      },
    ),
    assignRecruiter: fromPromise(
      async ({ input }: { input: { jobId: number; recruiterId: number } }) => {
        const res = await axios.patch(`/api/jobs/${input.jobId}/recruiters`, {
          recruiterId: input.recruiterId,
        });
        return res;
      },
    ),
    getCandidate: fromPromise(
      async ({ input }: { input: { jobId: number; candidateId: number } }) => {
        const res = await axios.get(
          `/api/jobs/${input.jobId}/candidates/${input.candidateId}`,
        );
        return res;
      },
    ),
    addFeedback: fromPromise(
      async ({
        input,
      }: {
        input: {
          jobId: number;
          candidateId: number;
          text: string;
          rating?: number;
          reason?: string;
        };
      }) => {
        const res = await axios.put(
          `/api/jobs/${input.jobId}/candidates/${input.candidateId}/feedbacks`,
          {
            text: input.text,
            ...(input.rating ? { rating: input.rating } : {}),
            ...(input.reason ? { reason: input.reason } : {}),
          },
        );
        return res;
      },
    ),
    getFeedback: fromPromise(
      async ({ input }: { input: { jobId: number; candidateId: number } }) => {
        try {
          const res = await axios.get(
            `/api/jobs/${input.jobId}/candidates/${input.candidateId}/feedbacks`,
          );
          return res;
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error?.response?.status === 404) {
            return { data: null };
          }
          // Rethrown as is: wrapping it in Error() dropped `response`, so the
          // error toast could not tell a 4xx message from anything else.
          throw error;
        }
      },
    ),
    sendCandidates: fromPromise(
      async ({
        input,
      }: {
        input: {
          jobId: number;
          candidateIds: React.Key[];
          emails: string[];
          emailBody: string;
        };
      }) => {
        const res = await axios.post(
          `/api/jobs/${input.jobId}/send-candidates`,
          {
            candidateIds: input.candidateIds,
            emails: input.emails,
            emailBody: input.emailBody,
          },
        );
        return res;
      },
    ),
    getCandidateDownloadCVUrl: fromPromise(
      async ({
        input,
      }: {
        input: {
          jobId: number;
          candidateId: number;
        };
      }) => {
        const res = await axios.get(
          `/api/jobs/${input.jobId}/candidates/${input.candidateId}/resume`,
        );
        return res;
      },
    ),
    updateCandidateStatus: fromPromise(
      async ({
        input,
      }: {
        input: {
          status: string;
          jobId: number;
          candidateId: number;
        };
      }) => {
        const res = await axios.patch(
          `/api/jobs/${input.jobId}/candidates/${input.candidateId}/status`,
          {
            status: input.status,
          },
        );
        return res;
      },
    ),
    shareCandidate: fromPromise(
      async ({
        input,
      }: {
        input: {
          jobId: number;
          candidateId: number;
          userId: number;
        };
      }) => {
        const res = await axios.patch(
          `/api/jobs/${input.jobId}/candidates/${input.candidateId}/send`,
          { userId: input.userId },
        );
        return res;
      },
    ),
    fetchJobAutopopulatedValues: fromPromise(
      async ({
        input,
      }: {
        input: {
          title: string;
          description: string;
        };
      }) => {
        const res = await axios.post(`/api/jobs/ai-extract`, {
          jobTitle: input.title,
          jobDescription: input.description,
        });
        return res;
      },
    ),
  },
});

// What the machine accepts when idle. ERROR accepts the same events, so a
// failed request never leaves the app unable to act until the page is reloaded.
const idleEvents = {
  [EventType.RESET_JOB_AUTO_POPULATED_VALUES]: {
    actions: jobsSetup.assign(() => ({
      autoPopulatedValues: {},
    })),
  },
  [EventType.FETCH_JOB_AUTO_POPULATED_VALUES]: {
    target: State.FETCH_JOB_AUTO_POPULATED_VALUES,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.FETCH_JOB_LIST]: {
    target: State.FETCH_JOB_LIST,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.CREATE_JOB]: {
    target: State.CREATE_JOB,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.FETCH_JOB_BY_ID]: {
    target: State.FETCH_JOB_BY_ID,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.UPDATE_JOB]: {
    target: State.UPDATE_JOB,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.FETCH_CANDIDATES]: {
    target: State.FETCH_CANDIDATES,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.SEND_CANDIDATES]: {
    target: State.SEND_CANDIDATES,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.FETCH_JOB_HIRING_MANAGERS]: {
    target: State.FETCH_JOB_HIRING_MANAGERS,
  },
  [EventType.FETCH_HIRING_MANAGERS]: {
    target: State.FETCH_HIRING_MANAGERS,
  },
  [EventType.ASSIGN_HIRING_MANAGER]: {
    target: State.ASSIGN_HIRING_MANAGER,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.FETCH_RECRUITERS]: {
    target: State.FETCH_RECRUITERS,
  },
  [EventType.ASSIGN_RECRUITER]: {
    target: State.ASSIGN_RECRUITER,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.GET_CANDIDATE]: {
    target: State.GET_CANDIDATE,
    actions: 'startCandidateLoad',
  },
  [EventType.ADD_CANDIDATE_FEEDBACK]: {
    target: State.ADD_CANDIDATE_FEEDBACK,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.GET_CANDIDATE_FEEDBACK]: {
    target: State.GET_CANDIDATE_FEEDBACK,
  },
  [EventType.FETCH_SHARED_CANDIDATES]: {
    target: State.FETCH_SHARED_CANDIDATES,
    actions: jobsSetup.assign(() => ({
      isLoading: true,
    })),
  },
  [EventType.UPDATE_CANDIDATE_STATUS]: {
    target: State.UPDATE_CANDIDATE_STATUS,
  },
  [EventType.GET_CANDIDATE_DOWNLOAD_CV_URL]: {
    target: State.GET_CANDIDATE_DOWNLOAD_CV_URL,
  },
  [EventType.SHARE_CANDIDATE]: {
    target: State.SHARE_CANDIDATE,
  },
} as const;

export const jobsFSM = jobsSetup.createMachine({
  id: MACHINE_ID,
  initial: State.INITIAL,
  context: {
    isLoading: false,
    jobList: [],
    candidates: [],
    sharedCandidates: [],
    hiringManagers: [],
    totalJobElements: 0,
    totalCandidateElements: 0,
    totalCandidatePages: 0,
    totalJobPages: 0,
    totalSharedCandidatePages: 0,
    totalSharedCandidateElements: 0,
    candidateDownloadCVUrl: '',
    autoPopulatedValues: {},
    candidateFeedback: null,
    jobDetails: null,
    candidate: undefined,
    requestedCandidateId: undefined,
    candidateLoadFailedFor: undefined,
    candidateLoadRefusal: undefined,
    error: undefined,
  },

  states: {
    [State.INITIAL]: {
      on: {
        ...idleEvents,
      },
    },
    [State.FETCH_JOB_AUTO_POPULATED_VALUES]: {
      invoke: {
        src: 'fetchJobAutopopulatedValues',
        input: ({ event }) => ({
          title: (event as fetchJobAutopopulatedValuesEvent).payload.title,
          description: (event as fetchJobAutopopulatedValuesEvent).payload
            .description,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            autoPopulatedValues: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.SHARE_CANDIDATE]: {
      invoke: {
        src: 'shareCandidate',
        input: ({ event }) => ({
          jobId: (event as shareCandidateEvent).payload.jobId,
          candidateId: (event as shareCandidateEvent).payload.candidateId,
          userId: (event as shareCandidateEvent).payload.userId,
        }),
        onDone: {
          target: State.CANDIDATE_SHARED,
          actions: assign({
            isLoading: false,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.GET_CANDIDATE_DOWNLOAD_CV_URL]: {
      invoke: {
        src: 'getCandidateDownloadCVUrl',
        input: ({ event }) => ({
          jobId: (event as getCandidateDownloadCVUrlEvent).payload.jobId,
          candidateId: (event as getCandidateDownloadCVUrlEvent).payload
            .candidateId,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            candidateDownloadCVUrl: ({ event }) => event.output.data.url,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.UPDATE_CANDIDATE_STATUS]: {
      invoke: {
        src: 'updateCandidateStatus',
        input: ({ event }) => ({
          status: (event as updateCandidateStatusEvent).payload.status,
          jobId: (event as updateCandidateStatusEvent).payload.jobId,
          candidateId: (event as updateCandidateStatusEvent).payload
            .candidateId,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            candidate: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_SHARED_CANDIDATES]: {
      invoke: {
        src: 'fetchSharedCandidates',
        input: ({ event }) => ({
          pageNumber: (event as fetchSharedCandidatesEvent).payload.pageNumber,
          pageSize: (event as fetchSharedCandidatesEvent).payload.pageSize,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            sharedCandidates: ({ event }) => event.output.data.sharedCandidates,
            totalSharedCandidatePages: ({ event }) =>
              event.output.data.totalPages,
            totalSharedCandidateElements: ({ event }) =>
              event.output.data.totalElements,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.GET_CANDIDATE_FEEDBACK]: {
      // Moving to another candidate while feedback is loading: restart as a
      // candidate load, which stops this request, instead of dropping the event.
      on: {
        [EventType.GET_CANDIDATE]: {
          target: State.GET_CANDIDATE,
          actions: 'startCandidateLoad',
        },
      },
      invoke: {
        src: 'getFeedback',
        input: ({ event }) => ({
          jobId: (event as getFeedbackEvent).payload.jobId,
          candidateId: (event as getFeedbackEvent).payload.candidateId,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            candidateFeedback: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.ADD_CANDIDATE_FEEDBACK]: {
      invoke: {
        src: 'addFeedback',
        input: ({ event }) => ({
          jobId: (event as addFeedbackEvent).payload.jobId,
          candidateId: (event as addFeedbackEvent).payload.candidateId,
          text: (event as addFeedbackEvent).payload.text,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.GET_CANDIDATE]: {
      // A second GET_CANDIDATE while one is in flight is a route change to
      // another candidate. Re-entering stops the running request, so the
      // earlier candidate's reply can never be applied. Without this the
      // event was dropped (no handler in this state) and the new candidate
      // was never requested at all.
      on: {
        [EventType.GET_CANDIDATE]: {
          target: State.GET_CANDIDATE,
          reenter: true,
          actions: 'startCandidateLoad',
        },
      },
      invoke: {
        src: 'getCandidate',
        input: ({ event }) => ({
          jobId: (event as getCandidateEvent).payload.jobId,
          candidateId: (event as getCandidateEvent).payload.candidateId,
        }),
        onDone: [
          {
            // Only a reply for the candidate that was asked for may be shown.
            guard: ({ context, event }) =>
              event.output.data?.id === context.requestedCandidateId,
            target: State.INITIAL,
            actions: assign({
              isLoading: false,
              candidate: ({ event }) => event.output.data,
              candidateDownloadCVUrl: '',
            }),
          },
          {
            target: State.INITIAL,
            actions: assign({
              isLoading: false,
              candidate: undefined,
              candidateLoadFailedFor: ({ context }) =>
                context.requestedCandidateId,
              candidateDownloadCVUrl: '',
            }),
          },
        ],
        // candidate was already cleared when the load started, so a failure
        // leaves nothing from the previous profile behind.
        onError: [
          {
            // A 403 is the server declining, not failing: the page says so in
            // place of the profile. Not an `error`, so no toast repeats it.
            guard: ({ event }) => candidateRefusal(event.error) !== undefined,
            target: State.INITIAL,
            actions: assign({
              isLoading: false,
              candidateLoadFailedFor: ({ context }) =>
                context.requestedCandidateId,
              candidateLoadRefusal: ({ event }) =>
                candidateRefusal(event.error),
            }),
          },
          {
            target: State.ERROR,
            actions: assign({
              isLoading: false,
              candidateLoadFailedFor: ({ context }) =>
                context.requestedCandidateId,
              error: ({ event }) => event.error as AxiosError,
            }),
          },
        ],
      },
    },
    [State.ASSIGN_HIRING_MANAGER]: {
      invoke: {
        src: 'assignHiringManagers',
        input: ({ event }) => ({
          jobId: (event as assignHiringmanagersEvent).payload.jobId,
          hiringManagerId: (event as assignHiringmanagersEvent).payload
            .hiringManagerId,
        }),
        onDone: {
          target: State.JOB_UPDATED,
          actions: assign({
            isLoading: false,
            jobDetails: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_HIRING_MANAGERS]: {
      invoke: {
        src: 'fetchHiringManagers',
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            hiringManagers: ({ event }) => event.output.data.users,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_RECRUITERS]: {
      invoke: {
        src: 'fetchRecruiters',
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            hiringManagers: ({ event }) => event.output.data.users,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.ASSIGN_RECRUITER]: {
      invoke: {
        src: 'assignRecruiter',
        input: ({ event }) => ({
          jobId: (event as assignRecruiterEvent).payload.jobId,
          recruiterId: (event as assignRecruiterEvent).payload.recruiterId,
        }),
        onDone: {
          target: State.JOB_UPDATED,
          actions: assign({
            isLoading: false,
            jobDetails: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_JOB_HIRING_MANAGERS]: {
      invoke: {
        src: 'fetchJobHiringManagers',
        input: ({ event }) => ({
          jobId: (event as fetchJobHiringmanagersEvent).payload.jobId,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            hiringManagers: ({ event }) => event.output.data.users,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.SEND_CANDIDATES]: {
      invoke: {
        src: 'sendCandidates',
        input: ({ event }) => ({
          jobId: (event as sendCandidatesEvent).payload.jobId,
          candidateIds: (event as sendCandidatesEvent).payload.candidateIds,
          emails: (event as sendCandidatesEvent).payload.emails,
          emailBody: (event as sendCandidatesEvent).payload.emailBody,
        }),
        onDone: {
          target: State.EMAIL_SENT,
          actions: assign({
            isLoading: false,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_CANDIDATES]: {
      invoke: {
        src: 'fetchCandidates',
        input: ({ event }) => ({
          jobId: (event as fetchCandidatesEvent).payload.jobId,
          pageNumber: (event as fetchCandidatesEvent).payload.pageNumber,
          pageSize: (event as fetchCandidatesEvent).payload.pageSize,
          fitScore: (event as fetchCandidatesEvent).payload.fitScore,
          statuses: (event as fetchCandidatesEvent).payload.statuses,
          hasMandatorySkills: (event as fetchCandidatesEvent).payload
            .hasMandatorySkills,
          hasMandatoryTitleMatch: (event as fetchCandidatesEvent).payload
            .hasMandatoryTitleMatch,
          maxDistance: (event as fetchCandidatesEvent).payload.maxDistance,
          candidatesWithNoLocation: (event as fetchCandidatesEvent).payload
            .candidatesWithNoLocation,
          isApplicant: (event as fetchCandidatesEvent).payload.isApplicant,
          sort: (event as fetchCandidatesEvent).payload.sort,
          order: (event as fetchCandidatesEvent).payload.order,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            candidates: ({ event }) => event.output.data.candidates,
            totalCandidatePages: ({ event }) => event.output.data.totalPages,
            totalCandidateElements: ({ event }) =>
              event.output.data.totalElements,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_JOB_LIST]: {
      invoke: {
        src: 'fetchJobList',
        input: ({ event }) => ({
          pageNumber: (event as fetchJobListEvent).payload.pageNumber,
          pageSize: (event as fetchJobListEvent).payload.pageSize,
          sort: (event as fetchJobListEvent).payload.sort,
          order: (event as fetchJobListEvent).payload.order,
          status: (event as fetchJobListEvent).payload.status,
          includeSynthetic: (event as fetchJobListEvent).payload
            .includeSynthetic,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            // A response missing `jobs`/`totalPages`/`totalElements` must not
            // leave these as undefined: PageHeading renders
            // `count.value.toLocaleString()` unconditionally once `count` is
            // passed, so an undefined totalJobElements crashes the whole
            // page rather than showing an empty list.
            jobList: ({ event }) =>
              Array.isArray(event.output.data?.jobs)
                ? event.output.data.jobs
                : [],
            totalJobPages: ({ event }) => event.output.data?.totalPages ?? 0,
            totalJobElements: ({ event }) =>
              event.output.data?.totalElements ?? 0,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.UPDATE_JOB]: {
      invoke: {
        src: 'updateJob',
        input: ({ event }) => ({
          id: (event as updateJobEvent).payload.id,
          job: (event as updateJobEvent).payload.job,
        }),
        onDone: {
          target: State.JOB_UPDATED,
          actions: assign({
            isLoading: false,
            jobDetails: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.CREATE_JOB]: {
      invoke: {
        src: 'createJob',
        input: ({ event }) => ({
          job: (event as createJobEvent).payload.job,
        }),
        onDone: {
          target: State.JOB_ADDED,
          actions: assign({
            isLoading: false,
            jobList: ({ event, context }) => [
              event.output.data,
              ...context.jobList,
            ],
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.FETCH_JOB_BY_ID]: {
      invoke: {
        src: 'fetchJobById',
        input: ({ event }) => ({
          id: (event as fetchJobByIdEvent).payload.id,
        }),
        onDone: {
          target: State.INITIAL,
          actions: assign({
            isLoading: false,
            jobDetails: ({ event }) => event.output.data,
          }),
        },
        onError: {
          target: State.ERROR,
          actions: assign({
            isLoading: false,
            error: ({ event }) => event.error as AxiosError,
          }),
        },
      },
    },
    [State.JOB_ADDED]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
    [State.JOB_UPDATED]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
    [State.EMAIL_SENT]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
    [State.CANDIDATE_SHARED]: {
      after: {
        200: {
          target: State.INITIAL,
        },
      },
    },
    [State.ERROR]: {
      // ERROR used to accept nothing (apart from GET_CANDIDATE), so one failed
      // request left every button and navigation dead until a reload.
      on: {
        ...idleEvents,
      },
    },
  },
});
